import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../services/api';
import { getFamilies as getSecureFamilies, setFamilies as setSecureFamilies } from '../utils/secureStorage';

const FamilyContext = createContext(null);

export function FamilyProvider({ children }) {
  const [activeFamilyId, setActiveFamilyId] = useState(null);
  const [families, setFamilies] = useState([]);
  const [primaryFamilyId, setPrimaryFamilyId] = useState(null);

  // Initialize from localStorage, then refresh from API
  useEffect(() => {
    const storedFamilyId = localStorage.getItem('familyId');
    const storedFamilies = getSecureFamilies();
    const storedPrimary = localStorage.getItem('primaryFamilyId');

    if (storedFamilyId) setActiveFamilyId(storedFamilyId);
    if (storedFamilies && storedFamilies.length > 0) {
      setFamilies(storedFamilies);
    }
    if (storedPrimary) setPrimaryFamilyId(storedPrimary);

    // Refresh families from API to pick up any membership changes
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    if (isLoggedIn) {
      axiosInstance.get('/account/families/detailed/')
        .then(res => {
          const apiFamilies = res.data?.families || [];
          if (apiFamilies.length > 0) {
            setFamilies(apiFamilies);
            setSecureFamilies(apiFamilies);

            // If current active family is no longer valid, switch to first available
            const currentId = storedFamilyId;
            const stillValid = apiFamilies.some(f => f.id === currentId);
            if (!stillValid) {
              const fallbackId = apiFamilies[0].id;
              setActiveFamilyId(fallbackId);
              localStorage.setItem('familyId', fallbackId);
            }
          }
        })
        .catch(() => {
          // Silently fail — localStorage data is still usable
        });
    }
  }, []);

  const activeFamily = families.find(f => f.id === activeFamilyId) || null;
  const currentRole = activeFamily?.role || null;
  const isMultiFamily = families.length > 1;

  const switchFamily = useCallback(async (familyId) => {
    // Update local state immediately
    setActiveFamilyId(familyId);
    localStorage.setItem('familyId', familyId);

    // Notify backend
    try {
      await axiosInstance.post('/account/switch-family/', { family_id: familyId });
    } catch (err) {
      console.error('Failed to notify backend of family switch:', err);
    }
  }, []);

  const updateFamilies = useCallback((newFamilies, newPrimaryId) => {
    setFamilies(newFamilies);
    setSecureFamilies(newFamilies);

    if (newPrimaryId) {
      setPrimaryFamilyId(newPrimaryId);
      localStorage.setItem('primaryFamilyId', newPrimaryId);
    }

    // If no active family or current active family is not in list, set to primary or first
    if (newFamilies.length > 0) {
      const currentValid = newFamilies.some(f => f.id === activeFamilyId);
      if (!currentValid) {
        const fallbackId = newPrimaryId || newFamilies[0].id;
        setActiveFamilyId(fallbackId);
        localStorage.setItem('familyId', fallbackId);
      }
    }
  }, [activeFamilyId]);

  const value = {
    activeFamilyId,
    activeFamily,
    families,
    currentRole,
    primaryFamilyId,
    isMultiFamily,
    switchFamily,
    updateFamilies,
  };

  return (
    <FamilyContext.Provider value={value}>
      {children}
    </FamilyContext.Provider>
  );
}

export function useFamily() {
  const context = useContext(FamilyContext);
  if (!context) {
    throw new Error('useFamily must be used within a FamilyProvider');
  }
  return context;
}

export default FamilyContext;
